import { useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  Hand,
  MapPin,
  PencilRuler,
  RotateCcw,
  ScanBarcode,
  Search,
  SearchX,
  TriangleAlert,
  Undo2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { MRS_CHIP_SHAPE } from '@/components/MrsLink';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { picklistService } from '../services/picklistService';
import type { PicklistDto } from '../types';
import type { PickHistoryDto } from '../types/pickHistory.types';
import {
  buildPickHistoryView,
  gridFor,
  layoutFor,
  summaryLine,
  type Marked,
  type PickHistoryKindFilter,
  type PickHistoryLayout,
  type PickHistoryRowView,
} from '../utils/pickHistoryModel';

/**
 * The picklist detail's Pick history (owner 2026-10-08; design handoff `design_handoff_pick_history`, README §1):
 * every pick-ledger entry of the picklist, grouped by Submit and Adjust, newest first, under the Items card. The
 * Activity panel tells the story; this is where a serial, an item or an MRS is looked up.
 *
 * It owns its fetch, so a keystroke in its search re-renders this card and nothing above it, and its failure never
 * touches the figures above. It refetches when any line's Picked figure changes (a Submit, an Adjust or a Move), keeping
 * what it shows until the new answer lands. The logic lives in `pickHistoryModel`, where vitest reaches it.
 */
export function PickHistoryCard({ picklist }: { picklist: PicklistDto }) {
  const [open, setOpen] = useState(true);
  const [history, setHistory] = useState<PickHistoryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [kind, setKind] = useState<PickHistoryKindFilter>('all');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // The ledger changes exactly when a line's Picked figure does.
  const ledgerKey = `${picklist.id}:${picklist.items.map((i) => `${i.id}=${i.quantityPicked}`).join(',')}`;
  useEffect(() => {
    let alive = true;
    setError(null);
    picklistService
      .getPickHistory(picklist.id)
      .then((data) => {
        if (alive) setHistory(data);
      })
      .catch((err) => {
        if (alive) setError(getApiErrorMessage(err, 'The server didn’t answer'));
      });
    return () => {
      alive = false;
    };
  }, [ledgerKey, picklist.id, reload]);

  // README §1: the columns follow the CARD's width, never the viewport's.
  const cardRef = useRef<HTMLElement>(null);
  const [layout, setLayout] = useState<PickHistoryLayout>('wide');
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const apply = () => setLayout(layoutFor(el.getBoundingClientRect().width));
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const loading = history === null && error === null;
  const failed = history === null && error !== null;
  const view = history ? buildPickHistoryView(history, deferredQuery, kind, expanded) : null;
  const grid = gridFor(layout);
  const empty = view !== null && view.total === 0;

  const headNote = open
    ? 'Every pick and reversal, newest first · adds up to Picked in Items'
    : loading
      ? 'Loading entries…'
      : failed
        ? 'Couldn’t load — open to retry'
        : history
          ? summaryLine(history)
          : '';

  const toggleGroup = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clearAll = () => {
    setQuery('');
    setKind('all');
  };

  return (
    <TooltipProvider delayDuration={200}>
      <section
        ref={cardRef}
        aria-label="Pick history"
        className="min-w-0 rounded-xl border border-border bg-card text-card-foreground shadow"
      >
        <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', open ? 'px-6 pb-3 pt-5' : 'px-6 py-4')}>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="-mx-2 -my-1 flex items-center gap-2 rounded-md px-2 py-1 text-left text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {open ? (
              <ChevronDown aria-hidden className="size-4 text-muted-foreground" />
            ) : (
              <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
            )}
            <h3 className="flex items-center gap-2 text-base font-semibold leading-none tracking-tight">
              Pick history
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums tracking-normal text-muted-foreground">
                {loading ? '…' : (view?.total ?? 0).toLocaleString('en-US')}
              </span>
            </h3>
          </button>
          <p className="min-w-0 flex-[1_1_240px] text-xs tabular-nums text-muted-foreground">{headNote}</p>
        </div>

        {open && (
          <>
            {view && view.total > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-6 pb-3">
                <div className="relative max-w-[320px] flex-[1_1_220px]">
                  <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search serial, item or MRS…"
                    aria-label="Search pick history by serial, item code or name, or MRS"
                    className={cn(
                      'h-8 w-full rounded-md border bg-background px-8 text-[13px] text-foreground outline-none',
                      query ? 'border-info bg-info/6 ring-[3px] ring-info/20' : 'border-input'
                    )}
                  />
                  {query && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Clear search"
                          onClick={() => setQuery('')}
                          className="absolute right-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <X aria-hidden className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Clear search</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div role="group" aria-label="Show entries" className="ml-auto inline-flex h-8 items-center rounded-lg bg-muted p-[3px] text-muted-foreground">
                  {(
                    [
                      ['all', 'All'],
                      ['picks', 'Picks'],
                      ['adjustments', 'Adjustments'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={kind === value}
                      onClick={() => setKind(value)}
                      className={cn(
                        'inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium',
                        kind === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                      )}
                    >
                      {label}
                      <span className="tabular-nums opacity-70">{view.counts[value].toLocaleString('en-US')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading && <HistorySkeleton />}

            {failed && (
              <div className="px-6 pb-6">
                <div role="alert" className="flex flex-wrap items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
                  <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
                  <div className="min-w-0 flex-[1_1_240px]">
                    <p className="text-sm font-semibold text-destructive">Couldn’t load the pick history</p>
                    <p className="mt-0.5 text-sm text-foreground">
                      {error?.replace(/[.\s]+$/, '')}. Items and totals above are not affected.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setReload((n) => n + 1)}>
                    <RotateCcw aria-hidden className="mr-1.5 size-3.5" />
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {empty && (
              <EmptyState
                icon={ScanBarcode}
                title="No picks yet"
                description="Picks submitted from the handheld appear here."
                className="mx-6 mb-6 rounded-lg border border-dashed border-border py-7"
              />
            )}

            {view && view.total > 0 && (
              <>
                <div className="max-h-[640px] overflow-y-auto overflow-x-hidden border-t border-border">
                  {grid.headAreas && (
                    // Opaque by two classes, never one `bg-[gradient,colour]` (UI-GUIDELINES §9).
                    <div
                      className="sticky top-0 z-[3] grid h-[33px] items-center border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))] px-4 text-xs font-medium text-muted-foreground"
                      style={{ gridTemplateColumns: grid.columns, gridTemplateAreas: grid.headAreas, columnGap: grid.gap }}
                    >
                      {grid.heads.map((h) => (
                        <span key={h.area} className={cn('min-w-0 truncate', h.end && 'text-right')} style={{ gridArea: h.area }}>
                          {h.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {view.noMatch && (
                    <div className="flex flex-col items-center gap-2.5 px-6 py-8 text-center">
                      <SearchX aria-hidden className="size-5 text-muted-foreground" />
                      <p className="text-sm">{view.noMatchText}</p>
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={clearAll}>
                        <X aria-hidden className="mr-1.5 size-3.5" />
                        {view.clearLabel}
                      </Button>
                    </div>
                  )}

                  {view.groups.map((g, gi) => (
                    <div key={g.id}>
                      <div
                        className={cn(
                          'sticky z-[2] flex items-start gap-2.5 border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.55),hsl(var(--muted)/0.55))] px-4 py-2',
                          grid.headAreas ? 'top-[33px]' : 'top-0',
                          gi > 0 && 'border-t'
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'flex size-[22px] shrink-0 items-center justify-center rounded-md',
                            g.kind === 'submit' ? 'bg-info/12 text-info' : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {g.kind === 'submit' ? <Hand className="size-3" /> : <PencilRuler className="size-3" />}
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <p className="text-[12.5px] leading-[22px] tabular-nums text-muted-foreground">
                            <b className="font-semibold text-foreground">{g.label}</b> · {g.meta}
                          </p>
                          {g.notes && (
                            <p className="text-xs text-foreground">
                              <span className="text-muted-foreground">Notes: </span>
                              {g.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {g.rows.map((r, ri) => (
                        <HistoryRow key={r.id} row={r} layout={layout} first={ri === 0} />
                      ))}

                      {g.moreText && (
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2">
                          <span className="text-xs tabular-nums text-muted-foreground">{g.moreText}</span>
                          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => toggleGroup(g.id)}>
                            {g.expanded ? (
                              <ChevronsUp aria-hidden className="mr-1.5 size-3.5" />
                            ) : (
                              <ChevronsDown aria-hidden className="mr-1.5 size-3.5" />
                            )}
                            {g.moreLabel}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <footer className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                  <span className="tabular-nums">
                    Showing {view.shown.toLocaleString('en-US')} of {view.total.toLocaleString('en-US')} entries
                  </span>
                  <span>From the pick ledger · times in PHT</span>
                </footer>
              </>
            )}
          </>
        )}
      </section>
    </TooltipProvider>
  );
}

/** The searched text with its match marked. */
function M({ m }: { m: Marked }) {
  return (
    <>
      {m.a}
      {m.b && <mark className="rounded-[2px] bg-info/22 p-0 text-inherit">{m.b}</mark>}
      {m.c}
    </>
  );
}

const MONO = 'font-mono';

/**
 * One ledger entry. Per-row hovers are native `title`s, never Radix tooltips (UI-GUIDELINES §8: one root per row is
 * what turned a cart tick into a 239ms long task); every cut-short cell carries its full text there.
 */
function HistoryRow({ row: r, layout, first }: { row: PickHistoryRowView; layout: PickHistoryLayout; first: boolean }) {
  const grid = gridFor(layout);
  return (
    <div
      className={cn('grid items-start gap-y-0.5 px-4 py-2 hover:bg-muted/35', !first && 'border-t border-border/60')}
      style={{ gridTemplateColumns: grid.columns, gridTemplateAreas: grid.rowAreas, columnGap: grid.gap }}
    >
      <time
        title={r.timeTitle}
        className={cn('flex flex-col whitespace-nowrap text-xs leading-[18px] tabular-nums text-muted-foreground', grid.timeEnd ? 'items-end justify-self-end' : 'items-start')}
        style={{ gridArea: 'time' }}
      >
        <span className="text-foreground">{r.date}</span>
        <span className="leading-4">{r.time}</span>
      </time>

      <div title={r.itemTitle} className="min-w-0" style={{ gridArea: 'item' }}>
        <p className={cn(MONO, 'truncate text-[12.5px] font-semibold leading-[18px]')}>
          <M m={r.code} />
        </p>
        <p className="truncate text-xs text-muted-foreground">
          <M m={r.name} />
        </p>
      </div>

      <div className="flex min-w-0 flex-col items-start gap-0.5" style={{ gridArea: 'mrs' }}>
        {r.materialRequestId > 0 ? (
          <Link
            to={`/mro/issuance/mrs/${r.materialRequestId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`${r.mrsNumber} — opens in a new tab`}
            className={cn(MRS_CHIP_SHAPE, 'max-w-full overflow-hidden text-ellipsis text-info transition-colors hover:bg-info/10')}
          >
            <M m={r.mrs} />
          </Link>
        ) : (
          <span className={cn(MRS_CHIP_SHAPE, 'text-muted-foreground')}>
            <M m={r.mrs} />
          </span>
        )}
        {r.mrsSub && (
          <span title={r.mrsSub} className="max-w-full truncate text-[11px] leading-[14px] text-muted-foreground">
            {r.mrsSub}
          </span>
        )}
      </div>

      <span title={r.bin} className={cn(MONO, 'flex min-w-0 items-center gap-1 text-xs leading-[18px]')} style={{ gridArea: 'bin' }}>
        <MapPin aria-hidden className="size-[11px] shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate">{r.bin}</span>
      </span>

      <div className="flex min-w-0 flex-col items-start" style={{ gridArea: 'serial' }}>
        {r.serial && (
          <span title={r.serialFull ?? undefined} className={cn(MONO, 'max-w-full truncate text-xs leading-[18px]')}>
            <M m={r.serial} />
          </span>
        )}
      </div>

      <span className="justify-self-end whitespace-nowrap text-[13px] font-semibold leading-[18px] tabular-nums" style={{ gridArea: 'qty' }}>
        {r.qty}
      </span>

      <div
        className={cn('flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1', grid.detailEnd ? 'justify-end' : 'justify-start')}
        style={{ gridArea: 'detail' }}
      >
        {r.isReversal && (
          <span
            title="A reversal written by Adjust on the web"
            className="inline-flex h-4 items-center rounded-md border border-border px-1.5 text-[10.5px] font-medium text-muted-foreground"
          >
            Adjusted
          </span>
        )}
        {r.disposition && !r.dispositionWarn && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs leading-[18px] text-muted-foreground">
            <Undo2 aria-hidden className="size-3" />
            {r.disposition}
          </span>
        )}
        {r.disposition && r.dispositionWarn && (
          <span
            tabIndex={0}
            title={r.dispositionTip ?? undefined}
            className="inline-flex cursor-help items-center gap-1 rounded-full bg-warning/12 px-2 py-px text-[11.5px] font-medium text-warning"
          >
            <TriangleAlert aria-hidden className="size-3" />
            {r.disposition}
          </span>
        )}
        {r.reading && <span className="truncate text-xs leading-[18px] text-muted-foreground">{r.reading}</span>}
      </div>
    </div>
  );
}

/** Loading: skeleton bars where the rows will be, never a spinner (UI gate 2). */
function HistorySkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading pick history" className="overflow-hidden rounded-b-xl border-t border-border">
      <div className="h-[33px] border-b border-border bg-muted/40" />
      <div className="flex items-center gap-2.5 border-b border-border bg-muted/25 px-4 py-2">
        <Skeleton className="size-[22px]" />
        <Skeleton className="h-3 w-[46%]" />
      </div>
      {[62, 48, 70, 55, 66, 44].map((w, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-border/60 px-4 py-3">
          <Skeleton className="h-3 w-16 shrink-0" />
          <div className="flex min-w-0 flex-[2] flex-col gap-1.5">
            <Skeleton className="h-3" style={{ width: `${w}%` }} />
            <Skeleton className="h-2.5" style={{ width: `${w - 18}%` }} />
          </div>
          <Skeleton className="h-[18px] flex-1" />
          <Skeleton className="h-3 flex-1" />
          <Skeleton className="h-3 w-12 shrink-0" />
        </div>
      ))}
    </div>
  );
}
